import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useIsAuthenticated } from '../lib/auth-store.js';
import { usePermissions } from '../lib/permissions.js';
import { UserMenu } from './user-menu.js';
import { ADMIN_TABS } from './admin-shell.js';

interface LayoutProps {
  children: ReactNode;
}

/**
 * Top-level chrome:
 *   - Brand on the left
 *   - Primary nav items in the middle (Dashboard, Calendar, Events, Campaigns,
 *     Contacts, Booking, and a single "Admin" hub link)
 *   - User menu on the right (display name + theme + sign out)
 *
 * On small screens the primary nav collapses into a hamburger drawer.
 * All previously-individual admin pages (Templates, Webhooks, Email, SMS,
 * Customer, Users, Roles) live inside the `/admin` hub now.
 */
export function Layout({ children }: LayoutProps): JSX.Element {
  const authed = useIsAuthenticated();
  const { can, data: perms } = usePermissions();
  const isAdmin = perms?.role === 'admin';
  // Show the "Admin" hub link whenever the user has access to ANY admin tab
  // (not just full-admin role) — otherwise delegated operators who could
  // previously reach Templates/Webhooks/etc directly from the top nav lose
  // their entry point now that those items are consolidated under /admin.
  const canSeeAdmin =
    isAdmin || ADMIN_TABS.some((t) => (t.section ? can(t.section, 'view') : false));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close the drawer on route change.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-full flex flex-col bg-surface text-ink">
      <header className="border-b border-border bg-surface-alt">
        <div className="mx-auto max-w-7xl px-4 h-16 sm:h-20 flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center shrink-0 whitespace-nowrap"
            aria-label="x-streamConnect home"
          >
            <img
              src="/logo-wordmark.svg"
              alt="x-streamConnect"
              className="hidden sm:block h-12 w-auto dark:brightness-125 dark:contrast-110"
            />
            <img
              src="/logo-mark.svg"
              alt="x-streamConnect"
              className="block sm:hidden h-10 w-10 dark:brightness-125 dark:contrast-110"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex flex-1 items-center gap-4 text-sm" aria-label="Primary">
            {authed && (
              <>
                <NavItem to="/" end>Dashboard</NavItem>
                <NavItem to="/calendar">Calendar</NavItem>
                {can('events', 'view') && <NavItem to="/events">Events</NavItem>}
                {can('campaigns', 'view') && <NavItem to="/campaigns">Campaigns</NavItem>}
                {can('contacts', 'view') && <NavItem to="/contact-profiles">Contacts</NavItem>}
                {can('service_profiles', 'view') && <NavItem to="/service-profiles">Booking</NavItem>}
                <NavItem to="/training">Training</NavItem>
                {canSeeAdmin && <NavItem to="/admin">Admin</NavItem>}
              </>
            )}
          </nav>

          {/* Right side: hamburger on mobile, user menu always */}
          <div className="ml-auto flex items-center gap-2">
            {authed && (
              <button
                type="button"
                className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded border border-border"
                aria-label="Open menu"
                aria-expanded={drawerOpen}
                onClick={() => setDrawerOpen((o) => !o)}
              >
                <svg viewBox="0 0 20 20" className="h-5 w-5"><path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
              </button>
            )}
            {authed && <UserMenu />}
          </div>
        </div>

        {/* Mobile drawer */}
        {authed && drawerOpen && (
          <nav
            className="md:hidden border-t border-border bg-surface-alt"
            aria-label="Primary mobile"
          >
            <div className="mx-auto max-w-7xl px-4 py-2 flex flex-col text-sm">
              <DrawerItem to="/" end>Dashboard</DrawerItem>
              <DrawerItem to="/calendar">Calendar</DrawerItem>
              {can('events', 'view') && <DrawerItem to="/events">Events</DrawerItem>}
              {can('campaigns', 'view') && <DrawerItem to="/campaigns">Campaigns</DrawerItem>}
              {can('contacts', 'view') && <DrawerItem to="/contact-profiles">Contacts</DrawerItem>}
              {can('service_profiles', 'view') && <DrawerItem to="/service-profiles">Booking</DrawerItem>}
              <DrawerItem to="/training">Training</DrawerItem>
              {canSeeAdmin && <DrawerItem to="/admin">Admin</DrawerItem>}
            </div>
          </nav>
        )}
      </header>
      <main className="flex-1">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:py-8">{children}</div>
      </main>
    </div>
  );
}

function NavItem({
  to,
  children,
  end,
}: {
  to: string;
  children: ReactNode;
  end?: boolean;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        isActive ? 'text-accent font-medium' : 'text-ink-muted hover:text-ink'
      }
    >
      {children}
    </NavLink>
  );
}

function DrawerItem({
  to,
  children,
  end,
}: {
  to: string;
  children: ReactNode;
  end?: boolean;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        'block py-2 ' + (isActive ? 'text-accent font-medium' : 'text-ink')
      }
    >
      {children}
    </NavLink>
  );
}
